import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for touch-based swipe navigation
 * Detects horizontal swipe gestures on mobile devices
 * 
 * @param {Function} onNavigate - Callback function (direction: 'prev' | 'next') => void
 * @param {boolean} enabled - Whether swipe navigation is enabled
 * @param {number} threshold - Minimum swipe distance in pixels (default: 50)
 * @returns {Object} { isSwiping, swipeDirection, swipeProgress }
 */
export const useSwipeNavigation = (onNavigate, enabled = true, threshold = 50) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchCurrentRef = useRef({ x: 0, y: 0 });
  const lastNavigationRef = useRef(0);
  const NAVIGATION_COOLDOWN = 1000;

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;

    const touch = e?.touches?.[0];
    if (!touch) return;

    touchStartRef.current = {
      x: touch?.clientX,
      y: touch?.clientY,
      time: Date.now()
    };
    touchCurrentRef.current = {
      x: touch?.clientX,
      y: touch?.clientY
    };
    setIsSwiping(true);
    setSwipeProgress(0);
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !isSwiping) return;

    const touch = e?.touches?.[0];
    if (!touch) return;

    touchCurrentRef.current = {
      x: touch?.clientX,
      y: touch?.clientY
    };

    const deltaX = touch?.clientX - touchStartRef?.current?.x;
    const deltaY = touch?.clientY - touchStartRef?.current?.y;

    // Only handle horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Prevent default to stop page scrolling during horizontal swipe
      e?.preventDefault();

      const direction = deltaX > 0 ? 'prev' : 'next';
      setSwipeDirection(direction);

      // Calculate progress (0 to 1)
      const progress = Math.min(Math.abs(deltaX) / threshold, 1);
      setSwipeProgress(progress);
    }
  }, [enabled, isSwiping, threshold]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !isSwiping) return;

    const deltaX = touchCurrentRef?.current?.x - touchStartRef?.current?.x;
    const deltaY = touchCurrentRef?.current?.y - touchStartRef?.current?.y;
    const deltaTime = Date.now() - touchStartRef?.current?.time;
    const velocity = Math.abs(deltaX) / deltaTime; // pixels per ms

    // Check if it's a horizontal swipe
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Trigger navigation if swipe distance exceeds threshold or velocity is high
      if (Math.abs(deltaX) >= threshold || velocity > 0.5) {
        const now = Date.now();
        const timeSinceLastNav = now - lastNavigationRef?.current;

        if (timeSinceLastNav >= NAVIGATION_COOLDOWN) {
          const direction = deltaX > 0 ? 'prev' : 'next';
          console.log('Swipe navigation triggered:', direction);
          if (typeof onNavigate === 'function') {
            onNavigate(direction);
          }
          lastNavigationRef.current = now;
        }
      }
    }

    // Reset state
    setIsSwiping(false);
    setSwipeDirection(null);
    setSwipeProgress(0);
  }, [enabled, isSwiping, threshold, onNavigate]);

  useEffect(() => {
    if (!enabled) {
      console.log('Swipe navigation disabled');
      return;
    }

    console.log('Swipe navigation enabled');
    // Add passive: false to allow preventDefault on touchmove
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    isSwiping,
    swipeDirection,
    swipeProgress
  };
};

export default useSwipeNavigation;